import { create } from 'zustand';
import { buildWsUrl } from '@/lib/api';

/**
 * Real-time view of the USB → Wi-Fi auto-switcher's per-device status.
 *
 * Single hub-wide WebSocket (`/api/wireless/autoswitch/events`). The
 * first frame is a snapshot — entries the SPA missed while it was
 * disconnected — and every subsequent frame is one of:
 *   - `start`   → `{ usbSerial, startedAt }`
 *   - `success` → `SwitchSuccessEvent`
 *   - `failed`  → `SwitchFailureEvent`
 *
 * Consumers (DeviceCard, Dashboard toast):
 *   - `useAutoswitch((s) => s.bySerial[serial])` for the current phase.
 *   - `useAutoswitch.getState().recentToasts` is a small ring buffer
 *     of (success | failed) events the Dashboard pops as toasts.
 *
 * Lifecycle:
 *   - `connect()` opens the WS. Idempotent — call from a top-level
 *     mount effect. Backoff reconnect on close (3 s, capped).
 *   - `disconnect()` for tests / sign-out.
 *
 * We deliberately keep this connection-per-tab and not per-device:
 *   - The bridge filters NIL server-side (it's a system-wide signal).
 *   - 1 socket / tab is cheap and matches the leases / batch pattern.
 */

/**
 * Tiered-transport (May 2026) BackupPath values that match
 * `src/wireless/UsbAdbBackupProvisioner.ts#BackupPath`:
 *   - `'wireless'` — Android 11+ wireless debugging (full ADB)
 *   - `'tcpip'`    — `adb tcpip 5555` mirror-only fallback
 *
 * Renamed from the legacy `'modern' | 'classic'`; server frames send
 * the new values verbatim and the SPA uses them as `path` labels in
 * the per-card badge and toast copy.
 */
export type BackupPath = 'wireless' | 'tcpip';

export interface AutoswitchSuccessFrame {
  usbSerial: string;
  wirelessSerial: string;
  endpoint: { host: string; port: number };
  hwId: string;
  path: BackupPath;
  startedAt: number;
  durationMs: number;
}

export interface AutoswitchFailureFrame {
  usbSerial: string;
  attemptedPaths: BackupPath[];
  message: string;
  modernReason?: string;
  startedAt: number;
  durationMs: number;
}

export type AutoswitchEntry =
  | { phase: 'switching'; usbSerial: string; startedAt: number }
  | (AutoswitchSuccessFrame & { phase: 'success' })
  | (AutoswitchFailureFrame & { phase: 'failed' });

/** One toast entry — drained by the Dashboard toast region. */
export interface AutoswitchToast {
  id: number;
  kind: 'success' | 'failed';
  entry: AutoswitchEntry;
  /** Wall-clock when the toast was pushed; consumers fade by age. */
  pushedAt: number;
}

const RECENT_TOAST_CAP = 6;
/** How long a `success`/`failed` entry stays visible on the device card. */
const TERMINAL_TTL_MS = 8_000;

interface AutoswitchState {
  bySerial: Record<string, AutoswitchEntry>;
  recentToasts: AutoswitchToast[];
  ws: WebSocket | null;
  connected: boolean;

  connect: () => void;
  disconnect: () => void;
  /** Drop a single toast by id (user dismiss / fade-out). */
  dismissToast: (id: number) => void;
  /** Drop the per-card entry for a serial (called after TTL expires). */
  clearEntry: (usbSerial: string) => void;
}

let toastSeq = 0;

export const useAutoswitch = create<AutoswitchState>((set, get) => {
  let reconnectTimer: number | null = null;

  const applyEntry = (entry: AutoswitchEntry): void => {
    set((cur) => ({
      bySerial: { ...cur.bySerial, [entry.usbSerial]: entry },
    }));
    if (entry.phase === 'success' || entry.phase === 'failed') {
      // Auto-expire terminal entries from the device card so the badge
      // doesn't linger forever — but only if no new switch starts in
      // the meantime (the next `start` event re-sets the slot).
      const seen = entry;
      const startedAt = entry.startedAt;
      window.setTimeout(() => {
        const cur = get().bySerial[seen.usbSerial];
        if (!cur) return;
        if (cur.phase === 'switching') return;
        if (cur.startedAt !== startedAt) return; // newer attempt landed
        get().clearEntry(seen.usbSerial);
      }, TERMINAL_TTL_MS);
    }
  };

  const pushToast = (kind: 'success' | 'failed', entry: AutoswitchEntry): void => {
    const id = ++toastSeq;
    set((cur) => {
      const next = [...cur.recentToasts, { id, kind, entry, pushedAt: Date.now() }];
      // Cap from the head so the most recent stay visible.
      if (next.length > RECENT_TOAST_CAP) next.splice(0, next.length - RECENT_TOAST_CAP);
      return { recentToasts: next };
    });
  };

  const open = (): void => {
    if (get().ws) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(buildWsUrl('/api/wireless/autoswitch/events'));
    } catch {
      scheduleReconnect();
      return;
    }
    set({ ws, connected: false });
    ws.onopen = (): void => set({ connected: true });
    ws.onmessage = (msg) => {
      let frame: { type: string; entries?: AutoswitchEntry[]; event?: unknown };
      try {
        frame = JSON.parse(typeof msg.data === 'string' ? msg.data : '') as typeof frame;
      } catch {
        return;
      }
      switch (frame.type) {
        case 'snapshot': {
          const byKey: Record<string, AutoswitchEntry> = {};
          for (const e of frame.entries ?? []) byKey[e.usbSerial] = e;
          set({ bySerial: byKey });
          return;
        }
        case 'start': {
          const e = frame.event as { usbSerial: string; startedAt: number } | undefined;
          if (!e || typeof e.usbSerial !== 'string') return;
          applyEntry({ phase: 'switching', usbSerial: e.usbSerial, startedAt: e.startedAt });
          return;
        }
        case 'success': {
          const e = frame.event as AutoswitchSuccessFrame | undefined;
          if (!e || typeof e.usbSerial !== 'string') return;
          const entry: AutoswitchEntry = { ...e, phase: 'success' };
          applyEntry(entry);
          pushToast('success', entry);
          return;
        }
        case 'failed': {
          const e = frame.event as AutoswitchFailureFrame | undefined;
          if (!e || typeof e.usbSerial !== 'string') return;
          const entry: AutoswitchEntry = { ...e, phase: 'failed' };
          applyEntry(entry);
          pushToast('failed', entry);
          return;
        }
      }
    };
    ws.onclose = (): void => {
      set({ ws: null, connected: false });
      scheduleReconnect();
    };
    ws.onerror = (): void => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  };

  const scheduleReconnect = (): void => {
    if (reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      // Only auto-reopen if `connect()` was still wanted (i.e. user
      // hasn't called `disconnect()`).
      if (get().ws === null) open();
    }, 3_000);
  };

  return {
    bySerial: {},
    recentToasts: [],
    ws: null,
    connected: false,

    connect: () => {
      open();
    },
    disconnect: () => {
      const ws = get().ws;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      set({ ws: null, connected: false });
    },
    dismissToast: (id) =>
      set((cur) => ({ recentToasts: cur.recentToasts.filter((t) => t.id !== id) })),
    clearEntry: (usbSerial) =>
      set((cur) => {
        if (!cur.bySerial[usbSerial]) return cur;
        const next = { ...cur.bySerial };
        delete next[usbSerial];
        return { bySerial: next };
      }),
  };
});
